// app/components/commonCodes/CommonCodeModal.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

const CommonCodeModal = ({ isOpen, onClose, onSave, code, codeType }) => {
    const [formData, setFormData] = useState({
        code: '',
        value: '',
        description: '',
        display_order: 0,
        is_active: true
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (code) {
            setFormData({
                code: code.code,
                value: code.value,
                description: code.description || '',
                display_order: code.display_order || 0,
                is_active: code.is_active
            });
        } else {
            setFormData({
                code: '',
                value: '',
                description: '',
                display_order: 0,
                is_active: true
            });
        }
        setErrors({});
    }, [code]);

    const validate = () => {
        const newErrors = {};

        if (!formData.code.trim()) {
            newErrors.code = 'Code is required';
        } else if (!/^[A-Z_0-9]+$/.test(formData.code)) {
            newErrors.code = 'Code must contain only uppercase letters, numbers, and underscores';
        }

        if (!formData.value.trim()) {
            newErrors.value = 'Value is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            await onSave(formData);
            onClose();
        } catch (error) {
            console.error('Error saving code:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCodeType = (type) => {
        return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={code ? 'Edit Code' : 'Add New Code'}
            size="md"
        >
            <div className="space-y-4">
                <div className='mb-7'>
                    <label className="block text-sm font-normal text-black mb-1">
                        Code Type
                    </label>
                    <input
                        type="text"
                        value={formatCodeType(codeType)}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4 pb-2">
                    <div>
                        <label className="block text-sm font-normal text-black mb-1">
                            Code
                        </label>
                        <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => setFormData({
                                ...formData,
                                code: e.target.value.toUpperCase()
                            })}
                            className={`outline-none w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.code ? 'border-red-500' : 'border-gray-300'
                                }`}
                            disabled={!!code}
                            placeholder="e.g., NEW_CODE"
                        />
                        {errors.code && (
                            <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-normal text-black mb-1">
                            Value
                        </label>
                        <input
                            type="text"
                            value={formData.value}
                            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                            className={`outline-none w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.value ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="Display value"
                        />
                        {errors.value && (
                            <p className="mt-1 text-sm text-red-600">{errors.value}</p>
                        )}
                    </div>
                </div>

                <div className='pb-2'>
                    <label className="block text-sm font-normal text-black mb-1">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="outline-none outline-none w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="2"
                        placeholder="Optional description"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-normal text-black mb-1">
                            Display Order
                        </label>
                        <input
                            type="number"
                            value={formData.display_order}
                            onChange={(e) => setFormData({
                                ...formData,
                                display_order: parseInt(e.target.value) || 0
                            })}
                            className="outline-none outline-none w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex items-end">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm font-normal text-gray-700">Active</span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-center gap-3 pt-5 pb-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : (code ? 'Update' : 'Create')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CommonCodeModal;